import type { AutomationType } from '@playbook-technologies/common-enums';
import * as Sentry from '@sentry/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useRef, useState } from 'react';

import { toAsync } from '@client/new/helpers/reactQuery';
import type { Async } from '@client/new/helpers/types';

import { AUTOMATION_ORDER } from '../automation-copy';
import {
  CreatorAutomationItem,
  CreatorAutomationResponse,
  getCreatorAutomations,
  updateCreatorAutomation,
} from '../automations.api';
import { automationKeys } from '../automations.keys';

export type AutomationsModel = {
  automations: CreatorAutomationItem[];
  pending: ReadonlySet<AutomationType>;
  errors: Partial<Record<AutomationType, AutomationsUpdateError>>;
  statuses: Partial<Record<AutomationType, AutomationsUpdateStatus>>;
  retry: () => Promise<void>;
  update: (automationType: AutomationType, enabled: boolean) => Promise<void>;
};

export type AutomationsRequestErrorKind =
  | 'unauthorized'
  | 'forbidden'
  | 'network';

export type AutomationsUpdateError = {
  kind: AutomationsRequestErrorKind;
  intendedEnabled: boolean;
};

export type AutomationsUpdateStatus = { kind: 'saving' | 'saved' };

export type AutomationsLoadError = {
  kind: AutomationsRequestErrorKind | 'contract';
  retry: () => Promise<void>;
};

class AutomationsContractError extends Error {}

const isKnownAutomationType = (value: string): value is AutomationType =>
  AUTOMATION_ORDER.some((type) => type === value);

const toKnownAutomation = (
  raw: CreatorAutomationResponse,
): CreatorAutomationItem | null => {
  const automationType = raw.automationType;
  if (!isKnownAutomationType(automationType)) {
    Sentry.captureMessage(
      `Unsupported creator automation type: ${automationType}`,
    );
    return null;
  }
  return { ...raw, automationType };
};

const toKnownAutomations = (
  response: CreatorAutomationResponse[],
): CreatorAutomationItem[] =>
  response.flatMap((raw) => {
    const item = toKnownAutomation(raw);
    return item ? [item] : [];
  });

const statusOf = (error: unknown): number | undefined =>
  isAxiosError(error) ? error.response?.status : undefined;

export const errorKindOf = (error: unknown): AutomationsRequestErrorKind => {
  switch (statusOf(error)) {
    case 401:
      return 'unauthorized';
    case 403:
      return 'forbidden';
    default:
      return 'network';
  }
};

const loadErrorKindOf = (error: unknown): AutomationsLoadError['kind'] =>
  error instanceof AutomationsContractError ? 'contract' : errorKindOf(error);

const withEnabled = (
  automations: CreatorAutomationItem[] | undefined,
  automationType: AutomationType,
  enabled: boolean,
): CreatorAutomationItem[] =>
  (automations ?? []).map((item) =>
    item.automationType === automationType ? { ...item, enabled } : item,
  );

const withRow = (
  automations: CreatorAutomationItem[] | undefined,
  row: CreatorAutomationItem,
): CreatorAutomationItem[] =>
  (automations ?? []).map((item) =>
    item.automationType === row.automationType ? row : item,
  );

type ToggleVariables = { automationType: AutomationType; enabled: boolean };

export const useAutomationsModel = (
  creatorId: number,
): Async<AutomationsModel, AutomationsLoadError> => {
  const queryClient = useQueryClient();
  const queryKey = automationKeys.list(creatorId);

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const known = toKnownAutomations(await getCreatorAutomations(creatorId));
      if (known.length === 0) {
        throw new AutomationsContractError('No supported automation types');
      }
      return known;
    },
    retry: false,
  });

  const [pending, setPending] = useState<ReadonlySet<AutomationType>>(
    new Set(),
  );
  const pendingRef = useRef<ReadonlySet<AutomationType>>(new Set());
  const [errors, setErrors] = useState<
    Partial<Record<AutomationType, AutomationsUpdateError>>
  >({});
  const [statuses, setStatuses] = useState<
    Partial<Record<AutomationType, AutomationsUpdateStatus>>
  >({});

  const releasePending = (automationType: AutomationType) => {
    const next = new Set(pendingRef.current);
    next.delete(automationType);
    pendingRef.current = next;
    setPending(next);
  };

  const mutation = useMutation<
    CreatorAutomationResponse,
    unknown,
    ToggleVariables
  >({
    mutationFn: ({ automationType, enabled }) =>
      updateCreatorAutomation(creatorId, automationType, enabled),
    retry: false,
    onSuccess: (canonical, { automationType }) => {
      const row = toKnownAutomation(canonical);
      if (!row) return;
      queryClient.setQueryData<CreatorAutomationItem[]>(queryKey, (current) =>
        withRow(current, row),
      );
      setStatuses((current) => ({
        ...current,
        [automationType]: { kind: 'saved' },
      }));
    },
    /**
     * Rollback flips `enabled` back instead of restoring a snapshot, which
     * holds only because the optimistic patch in `update` touches no other
     * field. Patch a second field there and this stops restoring it.
     */
    onError: (error, { automationType, enabled }) => {
      queryClient.setQueryData<CreatorAutomationItem[]>(queryKey, (current) =>
        withEnabled(current, automationType, !enabled),
      );
      setErrors((current) => ({
        ...current,
        [automationType]: {
          kind: errorKindOf(error),
          intendedEnabled: enabled,
        },
      }));
      setStatuses((current) => ({ ...current, [automationType]: undefined }));
    },
    onSettled: (_data, _error, { automationType }) =>
      releasePending(automationType),
  });

  const refetch = async () => {
    await query.refetch();
  };

  const data = toAsync(query, (error) => ({
    kind: loadErrorKindOf(error),
    retry: refetch,
  }));

  if (data.type !== 'fetched') return data;

  const update: AutomationsModel['update'] = async (
    automationType,
    enabled,
  ) => {
    if (pendingRef.current.has(automationType)) return;
    const exists = data.value.some(
      (item) => item.automationType === automationType,
    );
    if (!exists) return;

    pendingRef.current = new Set(pendingRef.current).add(automationType);
    setPending(pendingRef.current);
    setStatuses((current) => ({
      ...current,
      [automationType]: { kind: 'saving' },
    }));
    setErrors((current) => ({ ...current, [automationType]: undefined }));
    queryClient.setQueryData<CreatorAutomationItem[]>(queryKey, (current) =>
      withEnabled(current, automationType, enabled),
    );

    await mutation.mutateAsync({ automationType, enabled }).catch(() => {
      // Outcome is recorded in `errors` by onError; the View reads it per row.
    });
  };

  return {
    type: 'fetched',
    value: {
      automations: data.value,
      pending,
      errors,
      statuses,
      retry: refetch,
      update,
    },
  };
};
